import { useState, useRef, useEffect, useCallback } from 'react';
import { useAutoSave } from '@/hooks';
import { TrashIcon, Cog6ToothIcon, EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useConfirmDialog } from '../../../hooks/useConfirmDialog';
import { ConfirmDialog } from '../../shared/UIComponents';
import disciplineConfig from '../../../services/disciplineConfigService';
import EditableField from '../../shared/EditableField';
import MultiSelectDropdown from '../../shared/MultiSelectDropdown';
import SingleSelectDropdown from '../../shared/SingleSelectDropdown';
import { useSchool } from '@/contexts/SchoolContext';
import PeriodUndefinedBadge from '@/features/discipline/components/PeriodUndefinedBadge';
import { GOVERNED_RULE_TIMEFRAMES } from '@/services/domain/domainRuleValidation';
import { useRuleFormSnapshotSync } from './useRuleFormSnapshotSync';

// Helper function to get singular/plural form for options
const getPluralLabel = (options, value, optionValue) => {
  const option = options.find(opt => opt.value === optionValue);
  if (!option) return optionValue;
  // For config service objects, check for plural/singular properties
  if (value > 1) {
    return option.plural || option.label;
  }
  return option.singular || option.label;
};

const RULE_OPERATORS = Object.freeze({
  multiChoice: [
    { value: 'in', label: 'is one of' },
    { value: 'is-set', label: 'is recorded' },
    { value: 'is-empty', label: 'is left blank' },
  ],
  boolean: [
    { value: 'equals', label: 'is' },
    { value: 'is-set', label: 'is answered' },
    { value: 'is-empty', label: 'is unanswered' },
  ],
  number: [
    { value: 'lt', label: 'is below' },
    { value: 'lte', label: 'is at most' },
    { value: 'equals', label: 'equals' },
    { value: 'gte', label: 'is at least' },
    { value: 'gt', label: 'is above' },
    { value: 'is-set', label: 'is recorded' },
    { value: 'is-empty', label: 'is left blank' },
  ],
  text: [
    { value: 'is-set', label: 'is filled in' },
    { value: 'is-empty', label: 'is left blank' },
  ],
});

const defaultCondition = (field) => {
  if (!field) return null;
  if (field.type === 'multiChoice') {
    return { fieldId: field.id, operator: 'is-set', value: null };
  }
  if (field.type === 'boolean') {
    return { fieldId: field.id, operator: 'equals', value: true };
  }
  if (field.type === 'number') {
    return { fieldId: field.id, operator: 'lt', value: 0 };
  }
  return { fieldId: field.id, operator: 'is-set', value: null };
};

const resolvedRuleCondition = (rule, primaryField) => {
  if (rule?.eventCondition) return rule.eventCondition;
  if (
    primaryField?.type === 'multiChoice'
    && Array.isArray(rule?.mentionTypes)
    && rule.mentionTypes.length > 0
  ) {
    return {
      fieldId: primaryField.id,
      operator: 'in',
      value: [...rule.mentionTypes],
    };
  }
  return defaultCondition(primaryField);
};

const MentionRuleForm = ({
  rule = null,
  ruleId = null,
  categoryKey = null,
  onSave,
  onDelete,
  isRecognitionMode = false,
  domainEventTypeOptions = null,
  domainActionDefinitionOptions = null,
  domainRuleFieldOptions = null,
  domainLabel = null,
  repairMode = false,
}) => {
  const { confirmDialog, showConfirm } = useConfirmDialog();
  const { schoolId } = useSchool();
  const primaryRuleField = domainRuleFieldOptions?.[0] || null;

  // Initialize with proper defaults
  const [data, setData] = useState(() => {
    const defaults = {
      title: domainLabel ? `${domainLabel} response rule` : (isRecognitionMode ? 'Recognition Rule' : 'Mention Rule'),
      groups: [],
      historyFilter: null,
      count: 3,
      mentionScope: primaryRuleField?.type === 'multiChoice' ? 'same-offense' : 'any',
      mentionTypes: [], // Empty means all mention types
      timeCount: 1,
      timeframe: 'day',
      consequenceCount: 1,
      consequenceType: domainActionDefinitionOptions?.[0]?.value || 'demerit',
      consequenceTypeId: domainActionDefinitionOptions?.[0]?.id || null,
      resetCondition: 'after-consequence', // 'never', 'after-consequence', 'after-period'
      eventCondition: defaultCondition(primaryRuleField),
      active: true
    };

    if (!rule) return defaults;

    return {
      title: rule.title || defaults.title,
      groups: rule.groups || defaults.groups,
      historyFilter: rule.historyFilter || defaults.historyFilter,
      count: rule.count || defaults.count,
      mentionScope: rule.mentionScope || defaults.mentionScope,
      mentionTypes: rule.mentionTypes || defaults.mentionTypes,
      timeCount: rule.timeCount || defaults.timeCount,
      timeframe: rule.timeframe || defaults.timeframe,
      consequenceCount: rule.consequenceCount || defaults.consequenceCount,
      consequenceType: rule.consequenceType || defaults.consequenceType,
      consequenceTypeId: rule.consequenceTypeId || defaults.consequenceTypeId,
      resetCondition: rule.resetCondition || defaults.resetCondition,
      eventCondition: resolvedRuleCondition(rule, primaryRuleField),
      active: rule.active !== undefined ? rule.active : defaults.active,
      ...(rule.id && { id: rule.id })
    };
  });
  const { markLocalChanges, reconcileIncomingData } = useRuleFormSnapshotSync(data, setData);

  const [showConfig, setShowConfig] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [consequenceTypeOptions, setConsequenceTypeOptions] = useState([]);
  const [timeframeOptions, setTimeframeOptions] = useState([]);
  const [historyOptions, setHistoryOptions] = useState([]);
  const [gradeOptions, setGradeOptions] = useState([]);
  const [mentionTypeOptions, setMentionTypeOptions] = useState([]);

  useEffect(() => {
    if (repairMode) setShowConfig(true);
  }, [repairMode]);

  const menuRef = useRef();
  const isGovernedDomain = Array.isArray(domainEventTypeOptions);

  // Load consequence types from Firestore
  useEffect(() => {
    if (Array.isArray(domainActionDefinitionOptions)) {
      setConsequenceTypeOptions(domainActionDefinitionOptions);
      return undefined;
    }
    const loadConsequenceTypes = async () => {
      try {
        const types = await disciplineConfig.getConsequenceTypes(schoolId);
        setConsequenceTypeOptions(types.map(c => ({
          id: c.id,
          value: c.value,
          label: c.label,
          singular: c.singular,
          plural: c.plural
        })));
      } catch (error) {
        console.error('Error loading consequence types:', error);
      }
    };
    loadConsequenceTypes();
  }, [schoolId, domainActionDefinitionOptions]);

  // Load timeframes from Firestore
  useEffect(() => {
    if (isGovernedDomain) {
      setTimeframeOptions(GOVERNED_RULE_TIMEFRAMES);
      return undefined;
    }
    const loadTimeframes = async () => {
      try {
        const options = await disciplineConfig.getTimeframeOptions(schoolId);
        setTimeframeOptions(options);
      } catch (error) {
        console.error('Error loading timeframes:', error);
      }
    };
    loadTimeframes();
  }, [schoolId, isGovernedDomain]);

  // Load student history filters from Firestore
  useEffect(() => {
    const loadHistoryFilters = async () => {
      try {
        const options = await disciplineConfig.getStudentHistoryOptions();
        setHistoryOptions(options);
      } catch (error) {
        console.error('Error loading history filters:', error);
      }
    };
    loadHistoryFilters();
  }, []);

  // Load grades from Firestore
  useEffect(() => {
    const loadGrades = async () => {
      try {
        const options = await disciplineConfig.getGradeSelectOptions(schoolId);
        setGradeOptions(options);
      } catch (error) {
        console.error('Error loading grades:', error);
      }
    };
    loadGrades();
  }, [schoolId]);

  // Load mention types from Firestore
  useEffect(() => {
    const loadMentionTypes = async () => {
      if (Array.isArray(domainEventTypeOptions)) {
        setMentionTypeOptions(domainEventTypeOptions);
        return undefined;
      }
      try {
        const types = await disciplineConfig.getMentionTypes(schoolId);
        setMentionTypeOptions(types.map(t => ({
          value: t.value,
          label: t.label
        })));
      } catch (error) {
        console.error('Error loading mention types:', error);
      }
    };
    loadMentionTypes();
  }, [schoolId, domainEventTypeOptions]);

  // Reconcile every live snapshot, including updates to this same rule id.
  useEffect(() => {
    if (!rule?.id) return;
    reconcileIncomingData({
      title: rule.title || (domainLabel ? `${domainLabel} response rule` : (isRecognitionMode ? 'Recognition Rule' : 'Mention Rule')),
      groups: rule.groups || [],
      historyFilter: rule.historyFilter || null,
      count: rule.count || 3,
      mentionScope: rule.mentionScope || 'same-offense',
      mentionTypes: rule.mentionTypes || [],
      timeCount: rule.timeCount || 1,
      timeframe: rule.timeframe || 'day',
      consequenceCount: rule.consequenceCount || 1,
      consequenceType: rule.consequenceType || domainActionDefinitionOptions?.[0]?.value || 'demerit',
      consequenceTypeId: rule.consequenceTypeId || domainActionDefinitionOptions?.[0]?.id || null,
      resetCondition: rule.resetCondition || 'after-consequence',
      eventCondition: resolvedRuleCondition(rule, primaryRuleField),
      active: rule.active !== undefined ? rule.active : true,
      id: rule.id,
    });
  }, [rule, domainActionDefinitionOptions, domainLabel, isRecognitionMode, primaryRuleField, reconcileIncomingData]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const { save: debouncedSave } = useAutoSave(onSave, 100);

  const update = useCallback((updates) => {
    if (updates.consequenceType) {
      const option = consequenceTypeOptions.find(
        (candidate) => candidate.value === updates.consequenceType,
      );
      updates.consequenceTypeId = option?.id || null;
    }
    if (Array.isArray(updates.mentionTypes) && !updates.eventCondition && isGovernedDomain && primaryRuleField) {
      updates.eventCondition = updates.mentionTypes.length > 0
        ? {
            fieldId: data.eventCondition?.fieldId || primaryRuleField.id,
            operator: 'in',
            value: [...updates.mentionTypes],
          }
        : {
            fieldId: data.eventCondition?.fieldId || primaryRuleField.id,
            operator: 'is-set',
            value: null,
          };
    }
    markLocalChanges(updates);
    const newData = { ...data, ...updates };
    setData(newData);
    const actualRuleId = ruleId || rule?.id || newData.id;
    debouncedSave(newData, categoryKey || 'mention', actualRuleId);
  }, [data, debouncedSave, categoryKey, ruleId, rule?.id, consequenceTypeOptions, isGovernedDomain, markLocalChanges, primaryRuleField]);

  // Sortable setup
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: rule?.id || 'temp' });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };

  const getWhoText = () => {
    let text = data.groups.length > 0 ? data.groups.join(', ') : 'any student';
    return text;
  };

  const getHistoryText = () => {
    if (data.historyFilter === 'first-offense') return isRecognitionMode ? 'first recognition' : 'first offense';
    if (data.historyFilter === '2-plus') return isRecognitionMode ? '2+ recognitions' : '2+ offenses';
    if (data.historyFilter === '5-plus') return isRecognitionMode ? '5+ recognitions' : '5+ offenses';
    return null;
  };

  const getMentionTypeText = () => {
    if (isGovernedDomain && data.eventCondition) {
      const field = domainRuleFieldOptions?.find(
        (candidate) => candidate.id === data.eventCondition.fieldId,
      ) || primaryRuleField;
      const operator = (RULE_OPERATORS[field?.type] || []).find(
        (candidate) => candidate.value === data.eventCondition.operator,
      );
      if (data.eventCondition.operator === 'is-set' && field?.id === primaryRuleField?.id) {
        return 'all events';
      }
      if (data.eventCondition.operator === 'in') {
        const values = Array.isArray(data.eventCondition.value)
          ? data.eventCondition.value
          : [data.eventCondition.value].filter((value) => value != null);
        const labels = values.map((value) => (
          field?.options?.find((option) => option.value === String(value))?.label || String(value)
        ));
        return labels.length > 0 ? labels.join(' or ') : 'all events';
      }
      if (data.eventCondition.operator === 'equals' && field?.type === 'boolean') {
        return `${field.label} is ${data.eventCondition.value ? 'Yes' : 'No'}`;
      }
      const needsValue = !['is-set', 'is-empty'].includes(data.eventCondition.operator);
      return [field?.label, operator?.label, needsValue ? data.eventCondition.value : null]
        .filter((value) => value !== null && value !== undefined && value !== '')
        .join(' ');
    }
    if (!data.mentionTypes || data.mentionTypes.length === 0) {
      return isGovernedDomain ? 'all events' : (isRecognitionMode ? 'all recognitions' : 'all mentions');
    }
    if (data.mentionTypes.length === 1) {
      const type = mentionTypeOptions.find(t => t.value === data.mentionTypes[0]);
      return type ? `${type.label}` : (isGovernedDomain ? 'all events' : (isRecognitionMode ? 'all recognitions' : 'all mentions'));
    }
    if (isGovernedDomain) return `events (${data.mentionTypes.length} types)`;
    return isRecognitionMode ? `recognitions (${data.mentionTypes.length} types)` : `mentions (${data.mentionTypes.length} types)`;
  };

  // Use centralized configuration (sync methods - these don't query Firestore)
  const mentionScopeOptions = isGovernedDomain
    ? [
        { value: 'same-offense', label: 'for the same value' },
        { value: 'same-lesson', label: 'in the same lesson' },
        { value: 'any', label: 'across matching events' },
      ]
    : disciplineConfig.getMentionScopeOptions();
  const resetConditionOptions = disciplineConfig.getResetConditionOptions();
  const conditionField = domainRuleFieldOptions?.find(
    (field) => field.id === data.eventCondition?.fieldId,
  ) || primaryRuleField;
  const conditionOperators = RULE_OPERATORS[conditionField?.type] || [];
  const conditionNeedsValue = !['is-set', 'is-empty'].includes(data.eventCondition?.operator);
  const conditionFixesAnswer = data.eventCondition?.operator === 'equals'
    || (data.eventCondition?.operator === 'in'
      && Array.isArray(data.eventCondition.value)
      && data.eventCondition.value.length === 1);
  // An exact answer already groups matching events by that answer. Keep the
  // sentence clear without changing the saved scope or hiding a lesson scope.
  const scopeIsRedundant = isGovernedDomain && conditionFixesAnswer
    && ['same-offense', 'any'].includes(data.mentionScope);
  // consequenceTypeOptions, timeframeOptions, historyOptions, and gradeOptions are now loaded via useEffect above

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white rounded-lg shadow-sm ${!isDragging ? 'hover:shadow-md' : ''} relative transition-shadow`}
    >
      <div className="flex items-center p-4">
        {/* Drag Handle */}
        <div
          className="mr-2 py-1 cursor-grab hover:bg-base-200 rounded focus:outline-none"
          {...attributes}
          {...listeners}
          tabIndex={-1}
        >
          <svg
            className="w-4 h-4 text-base-content/50"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <circle cx="7" cy="5" r="1.5" />
            <circle cx="7" cy="10" r="1.5" />
            <circle cx="7" cy="15" r="1.5" />
            <circle cx="13" cy="5" r="1.5" />
            <circle cx="13" cy="10" r="1.5" />
            <circle cx="13" cy="15" r="1.5" />
          </svg>
        </div>

        <div className="flex-1">
          {/* Editable Title */}
          <div className="mb-3">
            <input
              key={rule?.id || 'new'} // Force re-render when rule changes
              type="text"
              defaultValue={data.title}
              onBlur={(e) => {
                const newTitle = e.target.value.trim() || (isRecognitionMode ? 'Recognition Rule' : 'Mention Rule');
                if (newTitle !== data.title) {
                  update({ title: newTitle });
                }
                setEditingTitle(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.target.blur();
                }
              }}
              className={`text-lg font-medium bg-transparent border-0 p-0 w-full outline-none cursor-pointer ${
                editingTitle ? 'text-black' : 'text-blue-600 hover:text-blue-700'
              }`}
              onFocus={() => setEditingTitle(true)}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="text-base pr-4">
              When <span className="font-medium text-blue-600">{getWhoText()}</span>
              {getHistoryText() && (
                <>
                  {' '}
                  <span
                    className="font-medium text-blue-600 px-1 -mx-0.5 rounded transition-colors cursor-pointer hover:bg-base-200"
                    onClick={() => {
                      // Cycle through history options (excluding null)
                      const validOptions = historyOptions.filter(opt => opt.value !== null);
                      const currentIndex = validOptions.findIndex(opt => opt.value === data.historyFilter);
                      const nextIndex = (currentIndex + 1) % validOptions.length;
                      update({ historyFilter: validOptions[nextIndex].value });
                    }}
                    title="Click to cycle history filter"
                  >
                    ({getHistoryText()})
                  </span>
                </>
              )} {isGovernedDomain ? 'records' : (isRecognitionMode ? 'earns' : 'gets')}{' '}
              <EditableField
                field="count"
                value={data.count}
                type="number"
                onUpdate={update}
                editingField={editingField}
                setEditingField={setEditingField}
                validation={{ min: 1, max: 999 }}
              />
              {' '}
              <span
                className="font-medium text-blue-600 px-1 -mx-0.5 rounded transition-colors cursor-pointer hover:bg-base-200"
                onClick={() => {
                  if (mentionTypeOptions.length === 0) return;

                  // Cycle through: all types -> each individual type -> all types
                  if (!data.mentionTypes || data.mentionTypes.length === 0) {
                    // Currently "all mentions", switch to first type
                    update({ mentionTypes: [mentionTypeOptions[0].value] });
                  } else if (data.mentionTypes.length === 1) {
                    // Find current type index and move to next
                    const currentIndex = mentionTypeOptions.findIndex(t => t.value === data.mentionTypes[0]);
                    const nextIndex = currentIndex + 1;
                    if (nextIndex < mentionTypeOptions.length) {
                      update({ mentionTypes: [mentionTypeOptions[nextIndex].value] });
                    } else {
                      // Back to "all mentions"
                      update({ mentionTypes: [] });
                    }
                  } else {
                    // Multiple types selected, go back to all
                    update({ mentionTypes: [] });
                  }
                }}
                title="Click to cycle mention types"
              >
                {getMentionTypeText()}
              </span>
              {!scopeIsRedundant && (
                <>
                  {' '}
                  <EditableField
                    field="mentionScope"
                    value={data.mentionScope}
                    options={mentionScopeOptions}
                    onUpdate={update}
                    editingField={editingField}
                    setEditingField={setEditingField}
                  />
                </>
              )}
              {data.mentionScope !== 'same-lesson' && (
                <>
                  {data.mentionScope === 'any' ? ' in ' : ' in '}
                  {data.mentionScope !== 'same-lesson' && (
                    <>
                      <EditableField
                        field="timeCount"
                        value={data.timeCount}
                        type="number"
                        onUpdate={update}
                        editingField={editingField}
                        setEditingField={setEditingField}
                        validation={{ min: 1, max: 999 }}
                      />
                      {' '}
                      <EditableField
                        field="timeframe"
                        value={data.timeframe}
                        options={timeframeOptions.map(opt => ({
                          value: opt.value,
                          label: getPluralLabel(timeframeOptions, data.timeCount, opt.value)
                        }))}
                        onUpdate={update}
                        editingField={editingField}
                        setEditingField={setEditingField}
                      />
                      <PeriodUndefinedBadge timeframe={data.timeframe} />
                    </>
                  )}
                </>
              )}
              , {isGovernedDomain ? 'create' : (isRecognitionMode ? 'award' : 'issue')}{' '}
              <EditableField
                field="consequenceCount"
                value={data.consequenceCount}
                type="number"
                onUpdate={update}
                editingField={editingField}
                setEditingField={setEditingField}
                validation={{ min: 1, max: 999 }}
              />
              {' '}
              <EditableField
                field="consequenceType"
                value={data.consequenceType}
                options={consequenceTypeOptions.map(opt => ({
                  value: opt.value,
                  label: getPluralLabel(consequenceTypeOptions, data.consequenceCount, opt.value)
                }))}
                onUpdate={update}
                editingField={editingField}
                setEditingField={setEditingField}
              />
            </div>

            <div className="flex items-center gap-2">
              {/* Active toggle */}
              <input
                type="checkbox"
                checked={data.active}
                onChange={(e) => update({ active: e.target.checked })}
                className={`toggle toggle-sm ${data.active ? 'toggle-success' : ''}`}
              />

              {/* Three dots menu */}
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setShowMenu(!showMenu)}
                  className="p-1.5 hover:bg-base-200 rounded"
                >
                  <EllipsisVerticalIcon className="w-4 h-4" />
                </button>

                <AnimatePresence>
                  {showMenu && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95, y: -10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: -10 }}
                      className="absolute right-0 mt-1 w-40 bg-white border border-base-200 rounded-lg shadow-lg z-10 overflow-hidden"
                    >
                      <button
                        onClick={() => {
                          setShowConfig(!showConfig);
                          setShowMenu(false);
                        }}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-base-100 flex items-center gap-2"
                      >
                        <Cog6ToothIcon className="w-4 h-4" />
                        {showConfig ? 'Less options' : 'More options'}
                      </button>
                      {onDelete && (
                        <button
                          onClick={async () => {
                            setShowMenu(false);
                            const confirmed = await showConfirm({
                              title: 'Delete Rule?',
                              message: 'Delete this rule permanently? This action cannot be undone.',
                              variant: 'danger',
                              confirmText: 'Delete',
                              cancelText: 'Cancel'
                            });
                            if (confirmed) {
                              const deleteId = ruleId || rule?.id || data.id;
                              if (deleteId) {
                                await onDelete(deleteId);
                              }
                            }
                          }}
                          className="w-full px-3 py-2 text-left text-sm hover:bg-error/10 text-error flex items-center gap-2"
                        >
                          <TrashIcon className="w-4 h-4" />
                          Delete
                        </button>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>

          <AnimatePresence mode="wait">
            {showConfig && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.15, ease: "easeInOut" }}
                style={{ overflow: 'hidden' }}
              >
                <div className="mt-4 space-y-4 border-t border-base-200 pt-4">
                  {isGovernedDomain && conditionField && (
                    <div className="grid gap-4 sm:grid-cols-3">
                      <div className="flex flex-col gap-2">
                        <label className="text-xs font-semibold uppercase tracking-wide" htmlFor={`rule-field-${rule?.id || 'new'}`}>
                          Event field
                        </label>
                        <select
                          id={`rule-field-${rule?.id || 'new'}`}
                          className="select select-bordered select-sm w-full"
                          value={domainRuleFieldOptions.some((field) => field.id === data.eventCondition?.fieldId)
                            ? data.eventCondition.fieldId
                            : ''}
                          onChange={(event) => {
                            const field = domainRuleFieldOptions.find((candidate) => candidate.id === event.target.value);
                            update({
                              eventCondition: defaultCondition(field),
                              mentionTypes: [],
                              mentionScope: field?.type === 'multiChoice' ? 'same-offense' : 'any',
                            });
                          }}
                        >
                          <option value="">Choose a field</option>
                          {domainRuleFieldOptions.map((field) => (
                            <option key={field.id} value={field.id}>{field.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className="text-xs font-semibold uppercase tracking-wide" htmlFor={`rule-operator-${rule?.id || 'new'}`}>
                          Condition
                        </label>
                        <select
                          id={`rule-operator-${rule?.id || 'new'}`}
                          className="select select-bordered select-sm w-full"
                          value={data.eventCondition?.operator || conditionOperators[0]?.value || ''}
                          onChange={(event) => update({
                            eventCondition: {
                              ...defaultCondition(conditionField),
                              ...data.eventCondition,
                              operator: event.target.value,
                              value: ['is-set', 'is-empty'].includes(event.target.value)
                                ? null
                                : data.eventCondition?.value,
                            },
                          })}
                        >
                          {conditionOperators.map((operator) => (
                            <option key={operator.value} value={operator.value}>{operator.label}</option>
                          ))}
                        </select>
                      </div>
                      {conditionNeedsValue && (
                        <div className="flex flex-col gap-2">
                          <label className="text-xs font-semibold uppercase tracking-wide" htmlFor={`rule-value-${rule?.id || 'new'}`}>
                            Value
                          </label>
                          {conditionField.type === 'multiChoice' ? (
                            <select
                              id={`rule-value-${rule?.id || 'new'}`}
                              className="select select-bordered select-sm w-full"
                              value={Array.isArray(data.eventCondition?.value)
                                ? data.eventCondition.value[0] || ''
                                : data.eventCondition?.value || ''}
                              onChange={(event) => update({
                                mentionTypes: event.target.value ? [event.target.value] : [],
                                eventCondition: {
                                  fieldId: conditionField.id,
                                  operator: 'in',
                                  value: event.target.value ? [event.target.value] : [],
                                },
                              })}
                            >
                              <option value="">Choose a value</option>
                              {conditionField.options.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                              ))}
                            </select>
                          ) : conditionField.type === 'boolean' ? (
                            <select
                              id={`rule-value-${rule?.id || 'new'}`}
                              className="select select-bordered select-sm w-full"
                              value={String(data.eventCondition?.value ?? true)}
                              onChange={(event) => update({
                                eventCondition: {
                                  ...data.eventCondition,
                                  fieldId: conditionField.id,
                                  value: event.target.value === 'true',
                                },
                              })}
                            >
                              <option value="true">Yes</option>
                              <option value="false">No</option>
                            </select>
                          ) : (
                            <input
                              id={`rule-value-${rule?.id || 'new'}`}
                              type="number"
                              className="input input-bordered input-sm w-full"
                              value={data.eventCondition?.value ?? ''}
                              onChange={(event) => update({
                                eventCondition: {
                                  ...data.eventCondition,
                                  fieldId: conditionField.id,
                                  value: event.target.value === '' ? null : Number(event.target.value),
                                },
                              })}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="grid gap-4 sm:grid-cols-2">
                    {scopeIsRedundant && (
                      <SingleSelectDropdown
                        label="COUNT MATCHING EVENTS"
                        value={data.mentionScope}
                        options={[
                          { value: data.mentionScope, label: 'Across the selected period' },
                          { value: 'same-lesson', label: 'In the same lesson' },
                        ]}
                        onChange={(mentionScope) => update({ mentionScope })}
                        allowClear={false}
                      />
                    )}
                    <MultiSelectDropdown
                      label="TARGET GROUPS"
                      value={data.groups}
                      options={gradeOptions}
                      onChange={(newGroups) => update({ groups: newGroups })}
                      placeholder="All grades"
                    />

                    {!isGovernedDomain && (
                      <MultiSelectDropdown
                        label="MENTION TYPES"
                        value={data.mentionTypes}
                        options={mentionTypeOptions}
                        onChange={(newMentionTypes) => update({ mentionTypes: newMentionTypes })}
                        placeholder="All types"
                      />
                    )}

                    <SingleSelectDropdown
                      label="STUDENT HISTORY"
                      value={data.historyFilter}
                      options={historyOptions}
                      onChange={(newHistory) => update({ historyFilter: newHistory })}
                      allowClear={true}
                    />

                    {!isGovernedDomain && (
                      <SingleSelectDropdown
                        label="RESET CONDITION"
                        value={data.resetCondition}
                        options={resetConditionOptions}
                        onChange={(newReset) => update({ resetCondition: newReset })}
                        allowClear={false}
                      />
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog {...confirmDialog} />
    </div>
  );
};

export default MentionRuleForm;
